import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui/SyutSelect';
import { SyutSearchGrid, type SyutSearchGridColumn, type SyutSearchGridFilter } from 'components/ui/SyutSearchGrid';

/**
 * ============================================================================
 * SERVICE MASTER MODAL COMPONENTS - REUSABLE
 * ============================================================================
 *
 * This file contains reusable modal components for Service Master:
 * - ServiceMasterFormModal: For creating, editing, and copying services
 * - ServiceMasterViewModal: For viewing service details
 * - ServiceMasterDeleteModal: For delete confirmation
 *
 * These components can be imported and used across different screens
 * (e.g., Service Master Screen, Service Invoice Screen, etc.)
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ServiceMasterRecord {
  id: string;
  code: string;
  serviceName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
  createdAt: string;
  createdBy: string;
}

export interface TaxDetail {
  id: string;
  taxType: string;
  taxCode: string;
  taxRate: string;
  jurisdiction: string;
}

export interface ServiceCategory {
  id: string;
  name: string;
  defaultEntities: string[];
  defaultGLAccount: string;
  defaultTaxes: TaxDetail[];
}

export interface GLAccount {
  id: string;
  code: string;
  name: string;
  category: string;
}

export interface Entity {
  id: string;
  code: string;
  name: string;
  type: 'Entity' | 'Business Unit';
}

export interface SampleMasterFormData {
  code: string;
  serviceName: string;
  description: string;
  category: string;
  entities: string[];
  uom: string;
  effectiveFrom: string;
  effectiveTo: string;
  status: 'Active' | 'Inactive';
  glAccount: string;
  taxDetails: TaxDetail[];
  scope: 'BU' | 'Entity' | 'Enterprise';
}

// ============================================================================
// SEARCHABLE DROPDOWN COMPONENT
// ============================================================================

interface SearchableDropdownProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="form-group">














      {label && <label className="form-label">{label}</label>}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            padding: '0.5rem 0.75rem',
            border: '1px solid #334155',
            borderRadius: '0.375rem',
            background: 'var(--bg-primary)',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span style={{ color: value ? '#1e293b' : '#94a3b8' }}>
            {value || placeholder}
          </span>
          <IconSystem.chevronDown size={16} />
        </div>

        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            left: 0,
            right: 0,
            maxHeight: '300px',
            background: 'var(--bg-primary)',
            border: '2px solid #2563eb',
            borderRadius: '0.5rem',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Search Input */}
            <div style={{ padding: '0.75rem', borderBottom: '1px solid #334155' }}>
              <div style={{ position: 'relative' }}>
                <IconSystem.search size={16} style={{
                  position: 'absolute',
                  left: '0.625rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search..."
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem 0.5rem 2.25rem',
                    border: '1px solid #334155',
                    borderRadius: '0.375rem',
                    fontSize: '0.85rem'
                  }}
                />
              </div>
            </div>

            {/* Options List */}
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '250px' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No options found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    style={{
                      padding: '0.625rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: 'var(--text-primary)',
                      background: value === option ? '#f8fafc' : 'transparent',
                      fontWeight: value === option ? 600 : 400
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = value === option ? '#f8fafc' : 'transparent';
                    }}
                  >
                    {option}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MULTI-SELECT DROPDOWN COMPONENT
// ============================================================================

interface MultiSelectDropdownProps {
  options: Entity[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  label?: string;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedIds,
  onChange,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(option =>
      option.name.toLowerCase().includes(query) ||
      option.code.toLowerCase().includes(query)
    );
  }, [options, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset highlighted index when dropdown opens/closes
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions.length]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsListRef.current) {
      const highlightedElement = optionsListRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const toggleSelection = useCallback((id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }, [selectedIds, onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => {
          const nextIndex = prev + 1;
          return nextIndex >= filteredOptions.length ? 0 : nextIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(prev => {
          const nextIndex = prev - 1;
          return nextIndex < 0 ? filteredOptions.length - 1 : nextIndex;
        });
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          toggleSelection(filteredOptions[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        setSearchQuery('');
        break;
      default:
        // Allow other keys to propagate for typing
        e.stopPropagation();
        break;
    }
  }, [filteredOptions, highlightedIndex, toggleSelection]);

  const toggleSelectAll = () => {
    const selectableIds = filteredOptions.map(opt => opt.id);
    const allFilteredSelected = selectableIds.every(id => selectedIds.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered options
      onChange(selectedIds.filter(id => !selectableIds.includes(id)));
    } else {
      // Select all filtered options (add to existing selection)
      onChange([...new Set([...selectedIds, ...selectableIds])]);
    }
  };

  const allFilteredSelected = filteredOptions.length > 0 &&
    filteredOptions.every(opt => selectedIds.includes(opt.id));

  // Get selected options with their details
  const selectedOptions = useMemo(() => {
    return options.filter(opt => selectedIds.includes(opt.id));
  }, [options, selectedIds]);

  // Remove a selected item
  const removeSelection = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedIds.filter(selectedId => selectedId !== id));
  }, [selectedIds, onChange]);

  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            minHeight: '38px',
            maxHeight: '120px',
            overflowY: 'auto',
            padding: '0.375rem 0.5rem',
            paddingRight: '2rem',
            border: '1px solid #334155',
            borderRadius: '0.375rem',
            background: 'var(--bg-primary)',
            cursor: 'pointer',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.25rem',
            alignItems: 'flex-start',
            alignContent: 'flex-start',
            position: 'relative'
          }}
        >
          {selectedOptions.length === 0 ? (
            <span style={{ color: '#94a3b8', fontSize: '0.85rem', padding: '0.125rem 0.25rem' }}>
              Select entities...
            </span>
          ) : (
            selectedOptions.map(option => (
              <span
                key={option.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.125rem 0.375rem',
                  background: '#e0ebf7',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.25rem',
                  fontSize: '0.75rem',
                  color: '#1e3a8a',
                  fontWeight: 500,
                  maxWidth: '150px'
                }}
              >
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {option.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => removeSelection(option.id, e)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '14px',
                    height: '14px',
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    color: '#64748b',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#cbd5e1';
                    e.currentTarget.style.color = '#1e293b';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <IconSystem.x size={10} />
                </button>
              </span>
            ))
          )}
          <IconSystem.chevronDown
            size={16}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b',
              flexShrink: 0
            }}
          />
        </div>

        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            left: 0,
            right: 0,
            maxHeight: '350px',
            background: 'var(--bg-primary)',
            border: '2px solid #2563eb',
            borderRadius: '0.5rem',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            zIndex: 10060,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Search Input */}
            <div style={{ padding: '0.75rem', borderBottom: '1px solid #334155' }}>
              <div style={{ position: 'relative' }}>
                <IconSystem.search size={16} style={{
                  position: 'absolute',
                  left: '0.625rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)'
                }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={handleKeyDown}
                  onMouseDown={(e) => e.stopPropagation()}
                  placeholder="Search entities..."
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem 0.5rem 2.25rem',
                    border: '1px solid #334155',
                    borderRadius: '0.375rem',
                    fontSize: '0.85rem',
                    background: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2563eb';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#334155';
                  }}
                />
              </div>
            </div>

            {/* Select All */}
            {filteredOptions.length > 0 && (
              <div
                onClick={toggleSelectAll}
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #334155',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: 'var(--primary)',
                  background: 'var(--bg-secondary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
              >
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  readOnly
                  style={{ width: '16px', height: '16px', accentColor: '#2563eb' }}
                />
                Select All {searchQuery && `(${filteredOptions.length} matching)`}
              </div>
            )}

            {/* Options List */}
            <div ref={optionsListRef} style={{ flex: 1, overflowY: 'auto', maxHeight: '200px' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No entities found
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isHighlighted = index === highlightedIndex;
                  return (
                    <div
                      key={option.id}
                      onClick={() => toggleSelection(option.id)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      style={{
                        padding: '0.625rem 1rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.85rem',
                        color: 'var(--text-primary)',
                        background: isHighlighted ? '#f8fafc' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(option.id)}
                        readOnly
                        style={{ width: '16px', height: '16px', accentColor: '#2563eb' }}
                      />
                      <div>
                        <div style={{ fontWeight: 500 }}>{option.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {option.code} • {option.type}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SIMILAR SERVICE NAME SUGGESTIONS
// ============================================================================

interface SimilarServiceSuggestionsProps {
  serviceName: string;
  existingServices: ServiceMasterRecord[];
  onSelectExisting: (service: ServiceMasterRecord) => void;
}

export const SimilarServiceSuggestions: React.FC<SimilarServiceSuggestionsProps> = ({
  serviceName,
  existingServices,
  onSelectExisting
}) => {
  const similarServices = useMemo(() => {
    if (!serviceName || serviceName.length < 3) return [];

    const query = serviceName.toLowerCase();
    return existingServices
      .filter(service =>
        service.serviceName.toLowerCase().includes(query) &&
        service.serviceName.toLowerCase() !== query
      )
      .slice(0, 5);
  }, [serviceName, existingServices]);

  if (similarServices.length === 0) return null;

  return (
    <div style={{
      marginTop: '0.5rem',
      padding: '0.75rem',
      background: '#fef3c7',
      border: '1px solid #fbbf24',
      borderRadius: '0.5rem'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '0.5rem'
      }}>
        <IconSystem.alert size={16} style={{ color: '#d97706' }} />
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>
          Similar services found in Enterprise
        </span>
      </div>
      <div style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '0.5rem' }}>
        You may map an existing service instead of creating a duplicate:
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {similarServices.map(service => (
          <div
            key={service.id}
            onClick={() => onSelectExisting(service)}
            style={{
              padding: '0.5rem',
              background: 'var(--bg-primary)',
              border: '1px solid #fbbf24',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#fffbeb';
              e.currentTarget.style.borderColor = '#f59e0b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#fbbf24';
            }}
          >
            <div style={{ fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
              {service.serviceName}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
              {service.code} • {service.category}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// SERVICE MASTER FORM MODAL
// ============================================================================

export interface SampleMasterFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  formData: SampleMasterFormData;
  onFormDataChange: (formData: SampleMasterFormData) => void;
  onSave: () => void;
  onClose: () => void;

  // Data sources
  categories: ServiceCategory[];
  entities: Entity[];
  glAccounts: GLAccount[];
  uoms: string[];
  taxServices: TaxDetail[];
  existingServices: ServiceMasterRecord[];

  // Optional callbacks
  onCategoryChange?: (categoryName: string, category?: ServiceCategory) => void;
  onSelectExistingService?: (service: ServiceMasterRecord) => void;

  // Current logged-in entity/company context
  currentEntityId?: string;
  currentEntityName?: string;

  // DEBUG: Track which component opened this modal
  openedBy?: string;
}

export const SampleMasterFormModal: React.FC<SampleMasterFormModalProps> = ({
  isOpen,
  mode,
  formData,
  onFormDataChange,
  onSave,
  onClose,
  categories,
  entities,
  glAccounts,
  uoms,
  taxServices,
  existingServices,
  onCategoryChange,
  onSelectExistingService,
  currentEntityId,
  currentEntityName,
  openedBy
}) => {
  const [isMappingMode, setIsMappingMode] = useState(false);
  const [selectedExistingService, setSelectedExistingService] = useState<ServiceMasterRecord | null>(null);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  // All hooks must be called before any conditional returns
  const handleCategoryChange = useCallback((categoryName: string) => {
    const category = categories.find(c => c.name === categoryName);

    if (onCategoryChange) {
      onCategoryChange(categoryName, category);
    } else if (category) {
      // Default behavior: cascade category defaults
      onFormDataChange({
        ...formData,
        category: categoryName,
        entities: category.defaultEntities,
        glAccount: category.defaultGLAccount,
        taxDetails: category.defaultTaxes
      });
    } else {
      onFormDataChange({ ...formData, category: categoryName });
    }
  }, [formData, onFormDataChange, categories, onCategoryChange]);

  const handleSelectExisting = useCallback((service: ServiceMasterRecord) => {
    if (onSelectExistingService) {
      onSelectExistingService(service);
    } else {
      // Auto-populate all fields from selected service
      setSelectedExistingService(service);
      setIsMappingMode(true);

      onFormDataChange({
        code: service.code,
        serviceName: service.serviceName,
        description: service.description,
        category: service.category,
        entities: currentEntityId ? [currentEntityId] : service.entities,
        uom: service.uom,
        effectiveFrom: service.effectiveFrom,
        effectiveTo: service.effectiveTo,
        status: service.status,
        glAccount: service.glAccount,
        taxDetails: service.taxDetails,
        scope: service.scope
      });
    }
  }, [onSelectExistingService, onFormDataChange, currentEntityId]);

  const handleSave = useCallback(() => {
    // Validation
    if (!formData.serviceName || !formData.category || !formData.uom) {
      alert('Please fill in all required fields');
      return;
    }

    // Check for exact duplicate at enterprise level (only in create mode, not mapping mode)
    if (mode === 'create' && !isMappingMode) {
      const exactMatch = existingServices.find(
        s => s.serviceName.toLowerCase() === formData.serviceName.toLowerCase() &&
        s.scope === 'Enterprise'
      );

      if (exactMatch) {
        alert('A service with this name already exists at Enterprise level. Please select it from the suggestions below or choose a different name.');
        return;
      }
    }

    // Single tax validation at entity level
    if (formData.scope === 'Entity' && formData.taxDetails.length > 1) {
      alert('Only one tax can be selected at Entity level');
      return;
    }

    onSave();

    // Reset mapping mode after save
    if (isMappingMode) {
      setIsMappingMode(false);
      setSelectedExistingService(null);
    }
  }, [formData, onSave, mode, isMappingMode, existingServices]);

  // Auto-select entity based on scope
  useEffect(() => {
    if (mode === 'create' && currentEntityId && !isMappingMode) {
      if (formData.scope === 'Entity' || formData.scope === 'BU') {
        // Auto-select current entity for entity/BU level masters
        if (!formData.entities.includes(currentEntityId)) {
          onFormDataChange({
            ...formData,
            entities: [currentEntityId]
          });
        }
      }
    }
  }, [formData.scope, currentEntityId, mode, isMappingMode]);

  // Reset mapping mode when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset mapping mode state when modal is closed
      setIsMappingMode(false);
      setSelectedExistingService(null);
    }
  }, [isOpen]);

  // F2 keyboard shortcut to open product search (only in create mode)
  useEffect(() => {
    if (!isOpen || mode !== 'create' || isMappingMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !productSearchOpen) {
        e.preventDefault();
        setProductSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, mode, isMappingMode, productSearchOpen]);

  // Early return after all hooks have been called
  if (!isOpen) return null;

  // Determine if entity field should be read-only
  const isEntityFieldReadOnly = (formData.scope === 'Entity' || formData.scope === 'BU') && currentEntityId;

  // DEBUG: Log when modal opens (disable in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔵 ServiceMasterFormModal opened', {
      openedBy: openedBy || 'Unknown Component',
      mode,
      formData: formData.serviceName || '(New Service)',
      timestamp: new Date().toISOString()
    });
    console.log('📍 Call stack:', new Error().stack);
  }

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">
            {isMappingMode && 'Map Existing Service to Entity'}
            {!isMappingMode && mode === 'create' && 'Add New Master'}
            {!isMappingMode && mode === 'edit' && 'Edit Service'}
            {!isMappingMode && mode === 'copy' && 'Copy Service'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        {isMappingMode && selectedExistingService && (
          <div style={{
            margin: '1rem 1.5rem 0',
            padding: '1rem',
            background: '#dcfce7',
            border: '1px solid #16a34a',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <IconSystem.check size={20} style={{ color: '#16a34a', flexShrink: 0 }} />
            <div style={{ fontSize: '0.875rem', color: '#166534' }}>
              <strong>Mapping Mode:</strong> You've selected an existing service from Enterprise level.
              Review the details below and click "Map to Entity" to add it to {currentEntityName || 'your entity'}.
            </div>
          </div>
        )}

        <div className="modal-content">
          <div className="form-group">
            <label className="form-label">Service Code *</label>
            <input
              type="text"
              className="form-input"
              value={formData.code}
              onChange={(e) => onFormDataChange({ ...formData, code: e.target.value })}
              placeholder="e.g., SVC007"
              style={{ border: '1px solid #334155' }}
              readOnly={isMappingMode}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Service Name *</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                className="form-input"
                value={formData.serviceName}
                onChange={(e) => {
                  onFormDataChange({ ...formData, serviceName: e.target.value });
                  // Reset mapping mode if user changes the name
                  if (isMappingMode) {
                    setIsMappingMode(false);
                    setSelectedExistingService(null);
                  }
                }}
                placeholder="e.g., Business Consulting"
                style={{ border: '1px solid #334155', flex: 1 }}
                readOnly={isMappingMode}
              />
              {!isMappingMode && mode === 'create' && (
                <button
                  type="button"
                  onClick={() => setProductSearchOpen(true)}
                  className="btn btn-secondary"
                  title="Search existing products (F2)"
                  style={{
                    padding: '0.5rem 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <IconSystem.search size={16} />
                  <span style={{ fontSize: '0.8rem' }}>F2</span>
                </button>
              )}
            </div>
            {!isMappingMode && (
              <SimilarServiceSuggestions
                serviceName={formData.serviceName}
                existingServices={existingServices}
                onSelectExisting={handleSelectExisting}
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
              placeholder="Detailed description of the service"
              rows={3}
              style={{ border: '1px solid #334155' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Service Category *</label>
            {isMappingMode ? (
              <input
                type="text"
                className="form-input"
                value={formData.category}
                readOnly
                style={{ border: '1px solid #334155', background: 'var(--bg-secondary)' }}
              />
            ) : (
              <SyutSelect
                options={categories.map(c => ({ value: c.name, label: c.name }))}
                value={formData.category}
                onChange={(val) => handleCategoryChange(val as string || '')}
                placeholder="Select or search category..."
                isSearchable
                isClearable
                size="md"
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Associated Entities / Business Units *
              {isEntityFieldReadOnly && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: '0.5rem', fontWeight: 400 }}>
                  (Auto-selected for {formData.scope} level)
                </span>
              )}
            </label>
            {isEntityFieldReadOnly || isMappingMode ? (
              <div style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid #334155',
                borderRadius: '0.375rem',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)'
              }}>
                {currentEntityName || entities.find(e => e.id === currentEntityId)?.name || formData.entities.map(id => entities.find(e => e.id === id)?.name || id).join(', ')}
              </div>
            ) : (
              <SyutSelect
                options={entities.map(e => ({ value: e.id, label: `${e.code} - ${e.name}` }))}
                value={formData.entities}
                onChange={(val) => onFormDataChange({ ...formData, entities: (val as string[]) || [] })}
                placeholder="Select entities..."
                searchPlaceholder="Search entities..."
                isMulti
                variant="checkbox"
                showSelectAll
                selectAllLabel="Select All"
                size="md"
              />
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Unit of Measure (UOM) *</label>
            {isMappingMode ? (
              <input
                type="text"
                className="form-input"
                value={formData.uom}
                readOnly
                style={{ border: '1px solid #334155', background: 'var(--bg-secondary)' }}
              />
            ) : (
              <SyutSelect
                options={uoms.map(u => ({ value: u, label: u }))}
                value={formData.uom}
                onChange={(val) => onFormDataChange({ ...formData, uom: val as string || '' })}
                placeholder="Select or search UOM..."
                isSearchable
                isClearable
                size="md"
              />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Effective From</label>
              <input
                type="date"
                className="form-input"
                value={formData.effectiveFrom}
                onChange={(e) => onFormDataChange({ ...formData, effectiveFrom: e.target.value })}
                style={{ border: '1px solid #334155' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Effective To</label>
              <input
                type="date"
                className="form-input"
                value={formData.effectiveTo}
                onChange={(e) => onFormDataChange({ ...formData, effectiveTo: e.target.value })}
                style={{ border: '1px solid #334155' }}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Default GL Account</label>
            <SyutSelect
              options={glAccounts.map(gl => ({ value: gl.id, label: `${gl.code} - ${gl.name}` }))}
              value={formData.glAccount}
              onChange={(val) => onFormDataChange({ ...formData, glAccount: val as string || '' })}
              placeholder="Select GL Account..."
              isSearchable
              isClearable
              size="md"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Tax Details</label>
            <div style={{
              padding: '0.75rem',
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '0.5rem',
              fontSize: '0.85rem',
              color: '#1e40af',
              marginBottom: '0.5rem'
            }}>
              <strong>Note:</strong> Only one tax can be selected at Entity level. Multiple taxes are allowed at Enterprise level.
            </div>
            <SyutSelect
              options={taxServices.map(tax => ({
                value: tax.id,
                label: `${tax.taxCode} - ${tax.taxRate} (${tax.taxType} - ${tax.jurisdiction})`
              }))}
              value={formData.taxDetails[0]?.id || ''}
              onChange={(val) => {
                const tax = taxServices.find(t => t.id === val);
                if (tax) {
                  onFormDataChange({ ...formData, taxDetails: [tax] });
                } else {
                  onFormDataChange({ ...formData, taxDetails: [] });
                }
              }}
              placeholder="Select Tax..."
              isSearchable
              isClearable
              size="md"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Scope</label>
            <SyutSelect
              options={[
                { value: 'BU', label: 'BU Only' },
                { value: 'Entity', label: 'Entity Level' },
                { value: 'Enterprise', label: 'Enterprise Level' }
              ]}
              value={formData.scope}
              onChange={(val) => onFormDataChange({ ...formData, scope: (val as 'BU' | 'Entity' | 'Enterprise') || 'Entity' })}
              placeholder="Select Scope..."
              isClearable={false}
              size="md"
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Services created at Entity level are automatically copied to Enterprise level for visibility
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Status</label>
            <SyutSelect
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'Inactive', label: 'Inactive' }
              ]}
              value={formData.status}
              onChange={(val) => onFormDataChange({ ...formData, status: (val as 'Active' | 'Inactive') || 'Active' })}
              placeholder="Select Status..."
              isClearable={false}
              size="md"
            />
          </div>
        </div>

        <div className="modal-footer">
          {isMappingMode && (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setIsMappingMode(false);
                setSelectedExistingService(null);
                onFormDataChange({
                  code: '',
                  serviceName: '',
                  description: '',
                  category: '',
                  entities: currentEntityId ? [currentEntityId] : [],
                  uom: '',
                  effectiveFrom: new Date().toISOString().split('T')[0],
                  effectiveTo: '',
                  status: 'Active',
                  glAccount: '',
                  taxDetails: [],
                  scope: 'Entity'
                });
              }}
            >
              <IconSystem.x size={16} />
              Cancel Mapping
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            <IconSystem.save size={16} />
            {isMappingMode ? 'Map to Entity' : mode === 'create' ? 'Create Service' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Product Search Popup - ERP-style F2 lookup */}
      <ProductSearchPopup<ServiceMasterRecord>
        isOpen={productSearchOpen}
        onClose={() => setProductSearchOpen(false)}
        onSelect={(service) => {
          handleSelectExisting(service);
          setProductSearchOpen(false);
        }}
        title="Search Existing Products/Services"
        data={existingServices}
        columns={[
          { key: 'code', header: 'Code', width: '100px', sortable: true },
          { key: 'serviceName', header: 'Name', width: '200px', sortable: true },
          { key: 'category', header: 'Category', width: '150px', sortable: true },
          { key: 'uom', header: 'UOM', width: '80px', sortable: true },
          {
            key: 'status',
            header: 'Status',
            width: '100px',
            sortable: true,
            render: (value: string) => (
              <span style={{
                padding: '0.125rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 500,
                background: value === 'Active' ? '#dcfce7' : '#fee2e2',
                color: value === 'Active' ? '#166534' : '#991b1b'
              }}>
                {value}
              </span>
            )
          },
          { key: 'scope', header: 'Scope', width: '100px', sortable: true },
        ]}
        filters={[
          {
            key: 'category',
            label: 'Category',
            type: 'select',
            options: categories.map(c => ({ value: c.name, label: c.name }))
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' }
            ]
          }
        ]}
        rowKey="id"
        searchPlaceholder="Search products/services..."
        recentlyUsedKey="modal-product-search"
        width="950px"
        height="600px"
      />
    </>
  );
};

// ============================================================================
// SERVICE MASTER VIEW MODAL
// ============================================================================

export interface ServiceMasterViewModalProps {
  isOpen: boolean;
  service: ServiceMasterRecord | null;
  onClose: () => void;
  onEdit?: (service: ServiceMasterRecord) => void;

  // Optional data sources for displaying related info
  glAccounts?: GLAccount[];
  entities?: Entity[];
}

export const ServiceMasterViewModal: React.FC<ServiceMasterViewModalProps> = ({
  isOpen,
  service,
  onClose,
  onEdit,
  glAccounts,
  entities
}) => {
  if (!isOpen || !service) return null;

  const glAccount = glAccounts?.find(gl => gl.id === service.glAccount);
  const entitiesDisplay = service.entities.map(eid => {
    const entity = entities?.find(e => e.id === eid);
    return entity?.name || eid;
  }).join(', ');

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Service Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label className="form-label">Service Code</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {service.code}
              </div>
            </div>
            <div>
              <label className="form-label">Service Name</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {service.serviceName}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Description</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {service.description}
              </div>
            </div>
            <div>
              <label className="form-label">Category</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {service.category}
              </div>
            </div>
            <div>
              <label className="form-label">UOM</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {service.uom}
              </div>
            </div>
            <div>
              <label className="form-label">Status</label>
              <span className={`badge badge-${service.status.toLowerCase()}`}>
                {service.status}
              </span>
            </div>
            <div>
              <label className="form-label">Scope</label>
              <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                {service.scope}
              </div>
            </div>
            {entities && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Associated Entities</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                  {entitiesDisplay}
                </div>
              </div>
            )}
            {glAccount && (
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">GL Account</label>
                <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem' }}>
                  {glAccount.code} - {glAccount.name}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(service);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// SERVICE MASTER DELETE MODAL
// ============================================================================

export interface ServiceMasterDeleteModalProps {
  isOpen: boolean;
  service: ServiceMasterRecord | null;
  onConfirm: () => void;
  onClose: () => void;
}

export const ServiceMasterDeleteModal: React.FC<ServiceMasterDeleteModalProps> = ({
  isOpen,
  service,
  onConfirm,
  onClose
}) => {
  if (!isOpen || !service) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header">
          <h2 className="modal-title">Confirm Delete</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <p style={{ marginBottom: '1rem' }}>
            Are you sure you want to delete <strong>{service.serviceName}</strong>?
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            This action cannot be undone.
          </p>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            <IconSystem.trash size={16} />
            Delete
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// PRODUCT SEARCH POPUP - ERP-STYLE F2/CTRL+L LOOKUP
// ============================================================================

export interface ProductSearchPopupProps<T = any> {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (record: T) => void;
  title?: string;
  data: T[];
  columns?: SyutSearchGridColumn<T>[];
  filters?: SyutSearchGridFilter[];
  rowKey?: keyof T | string;
  searchPlaceholder?: string;
  width?: string;
  height?: string;
  enableRecentlyUsed?: boolean;
  recentlyUsedKey?: string;
  /** Z-index for the popup (default: 10050 to appear above modals) */
  zIndex?: number;
}

export function ProductSearchPopup<T extends Record<string, any>>({
  isOpen,
  onClose,
  onSelect,
  title = 'Product Search',
  data,
  columns,
  filters,
  rowKey = 'id',
  searchPlaceholder = 'Search products... (F2 to focus)',
  width = '900px',
  height = '600px',
  enableRecentlyUsed = true,
  recentlyUsedKey = 'product-search',
  zIndex = 10050,
}: ProductSearchPopupProps<T>) {
  // Default columns if not provided
  const defaultColumns: SyutSearchGridColumn<T>[] = useMemo(() => [
    { key: 'code', header: 'Code', width: '100px', sortable: true },
    { key: 'productName', header: 'Product Name', width: '200px', sortable: true },
    { key: 'category', header: 'Category', width: '150px', sortable: true },
    { key: 'uom', header: 'UOM', width: '80px', sortable: true },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      sortable: true,
      render: (value: string) => (
        <span style={{
          padding: '0.125rem 0.5rem',
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          background: value === 'Active' ? '#dcfce7' : '#fee2e2',
          color: value === 'Active' ? '#166534' : '#991b1b'
        }}>
          {value}
        </span>
      )
    },
  ], []);

  // Default filters if not provided
  const defaultFilters: SyutSearchGridFilter[] = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' }
      ]
    },
  ], []);

  return (
    <SyutSearchGrid<T>
      isOpen={isOpen}
      onClose={onClose}
      onSelect={onSelect}
      title={title}
      columns={columns || defaultColumns}
      data={data}
      rowKey={rowKey as string}
      filters={filters || defaultFilters}
      enableRecentlyUsed={enableRecentlyUsed}
      recentlyUsedKey={recentlyUsedKey}
      searchPlaceholder={searchPlaceholder}
      showRowNumbers
      width={width}
      height={height}
      zIndex={zIndex}
    />
  );
}
